import {
	CALIBRATION_STORAGE_KEY_PREFIX,
	MAX_CALIBRATION_RECORDS,
	CALIBRATION_SCHEMA_VERSION,
} from "./constants"
import { buildEnvSignature, captureEnvironment } from "./utils"
import type { EEGCalibrationRecord } from "./types"

type LoadedCalibration = { key: string; record: EEGCalibrationRecord }

const parseCalibration = (raw: string | null): EEGCalibrationRecord | undefined => {
	if (!raw) return undefined

	try {
		const parsed = JSON.parse(raw)

		if (
			typeof parsed.pxPerMm !== "number" ||
			!Number.isFinite(parsed.pxPerMm) ||
			parsed.pxPerMm <= 0 ||
			typeof parsed.savedAt !== "string" ||
			Number.isNaN(Date.parse(parsed.savedAt)) ||
			typeof parsed.version !== "number" ||
			typeof parsed.env !== "object" ||
			parsed.env === null
		) {
			return undefined
		}

		if (parsed.version !== CALIBRATION_SCHEMA_VERSION) {
			console.warn("[EEG Calibration] Unknown calibration schema version")
			return undefined
		}

		return {
			pxPerMm: parsed.pxPerMm,
			savedAt: parsed.savedAt,
			env: parsed.env,
			version: parsed.version,
		}
	} catch (error) {
		console.warn("[EEG Calibration] Failed to parse calibration payload", error)
		return undefined
	}
}

const getCalibrationEntries = (): LoadedCalibration[] => {
	const entries: LoadedCalibration[] = []

	for (let index = 0; index < window.localStorage.length; index += 1) {
		const key = window.localStorage.key(index)
		if (!key?.startsWith(CALIBRATION_STORAGE_KEY_PREFIX)) continue

		const record = parseCalibration(window.localStorage.getItem(key))
		if (record) entries.push({ key, record })
	}

	return entries
}

// Deletes old calibrations if there are more than MAX_CALIBRATION_RECORDS, excluding `keep`
const pruneCalibrationStorage = (keep?: string) => {
	const items = getCalibrationEntries()
	items.sort((first, second) => {
		const firstTime = new Date(first.record.savedAt).getTime() || 0
		const secondTime = new Date(second.record.savedAt).getTime() || 0
		return secondTime - firstTime
	})

	// keep only the MAX_CALIBRATION_RECORDS most recent, and cleanup old schema versions
	items.forEach((item, idx) => {
		const shouldDelete =
			item.key !== keep &&
			(idx >= MAX_CALIBRATION_RECORDS || item.record.version !== CALIBRATION_SCHEMA_VERSION)

		if (shouldDelete) window.localStorage.removeItem(item.key)
	})
}

export const loadCalibration = (): LoadedCalibration | undefined => {
	const env = captureEnvironment()
	if (!env) {
		return undefined
	}

	const signature = buildEnvSignature(env)
	const key = `${CALIBRATION_STORAGE_KEY_PREFIX}${signature}`
	const record = parseCalibration(window.localStorage.getItem(key))
	return record ? { key, record } : undefined
}

export const saveCalibration = (pxPerMm: number): LoadedCalibration | undefined => {
	const env = captureEnvironment()
	if (!env) {
		return undefined
	}

	const signature = buildEnvSignature(env)
	const record: EEGCalibrationRecord = {
		pxPerMm,
		env,
		savedAt: new Date().toISOString(),
		version: CALIBRATION_SCHEMA_VERSION,
	}

	try {
		const key = `${CALIBRATION_STORAGE_KEY_PREFIX}${signature}`
		window.localStorage.setItem(key, JSON.stringify(record))
		pruneCalibrationStorage(key)

		return { key, record }
	} catch (error) {
		console.error("[EEG Calibration] Failed to save calibration:", error)
		return undefined
	}
}

export const clearCalibration = (key: string) => {
	window.localStorage.removeItem(key)
	pruneCalibrationStorage()
}
